// Link OB modal — restores the historical "Link OB" action so a NEW matter
// (typically a secondary matter type such as ILA) can pull OB data from
// an EXISTING matter without re-keying client identity / contact /
// address.
//
// Trust-context rules surfaced in this UI:
//   - The current matter ref is shown READ-ONLY at the top of the modal
//     and the API guarantees it is never mutated by link/unlink. The UI
//     re-asserts the current ref after the response.
//   - Search uses /api/smart-search (unified client/matter resolver). It
//     filters to active matters only (closed/archived/pending excluded
//     at the data source — see hub/matter_status.py). No fuzzy fallback;
//     the user picks an exact existing matter from the suggestion list.
//   - Historical bug: an earlier version pointed at /api/search-resolve
//     which does not exist — the call 404'd and the dropdown silently
//     showed "No matters found" for every query. Pinned by
//     LinkOBModal.search-endpoint.test.js.
//   - On success the response lists which fields were copied and which
//     were skipped because the current matter already had a value. We
//     surface both lists so the fee earner can see exactly what
//     changed.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const getHeaders = () => {
  const uid =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    "";
  // Session is identified by X-Bonsai-User-Id (set after Microsoft SSO via
  // /api/auth/callback). The legacy X-Session-Token: shared header has been
  // removed — session_guard now requires a real per-user session.
  const h = {};
  if (uid) h["X-Bonsai-User-Id"] = uid;
  if (email) h["X-User-Email"] = email;
  return h;
};

// ─── Modal ────────────────────────────────────────────────────────────────────
// Audit 05 P0-4 / BM-H466 / BM-H4342: what the link did NOT do, in words a
// fee earner can act on. Shared by the first link and by "Refresh
// inheritance", which until BM-H4342 threw these away and showed nothing.
export function linkOutcomeWarnings(data) {
  const warnings = [];
  if (data?.pipeline_advanced === false) {
    warnings.push({
      key: "pipeline",
      label: "Pipeline did not advance",
      detail:
        data?.pipeline_advance_error ||
        "advance_pipeline() reported no change — the tracker may still show OB Sent.",
    });
  }
  if (data?.ob_satisfied === false) {
    warnings.push({
      key: "ob",
      label: "OB satisfaction stub not written",
      detail:
        data?.ob_satisfied_skip_reason ||
        "The linked matter's OB was not marked satisfied on the current matter.",
    });
  }
  // BM-H466 (Vanessa, 9 Sep 2026), BL.VEC.006.1507 linked to
  // BL.VEC.006.0528. The API has always returned aml_inherited and, when
  // it is false, the plain words reason. This modal read neither, so an
  // operator whose link satisfied the OB but could not carry the AML saw a
  // clean green "Linked" and was told nothing. That matter then stopped at
  // the AML gate with no draft file opening request, and the client waited.
  if (data?.aml_inherited === false && data?.aml_inherit_skip_reason) {
    warnings.push({
      key: "aml",
      label: "AML did not carry across from the linked matter",
      detail: data.aml_inherit_skip_reason,
    });
  }
  if (data?.pdf_copy_error) {
    warnings.push({
      key: "pdf",
      label: "OB PDFs did not copy across",
      detail: data.pdf_copy_error,
    });
  }
  // BM-H4342 (15 Oct 2026, BL.RM.001.1563): the retainer never travels on a
  // link, so the client is asked to accept terms on THIS matter. When that
  // request could not be sent, nobody was told and the client was never
  // asked. The backend has always returned the reason; say it.
  if (data?.terms_requested === false
      && data?.terms_request_skip_reason
      && data.terms_request_skip_reason !== "already_accepted"
      // BM-H4488: a second press does not send the terms again.
      && data.terms_request_skip_reason !== "already_sent") {
    warnings.push({
      key: "terms",
      label: "Client has not been asked to accept the Terms of Business on this matter",
      detail: data.terms_request_skip_reason === "no_client_email"
        ? "There is no client email address on this matter's onboarding record. Add it with the pencil beside the client's name (Correct Client Details), then press Refresh inheritance."
        : data.terms_request_skip_reason,
    });
  }
  return warnings;
}

export function LinkOBModal({ matterRef, onClose, onLinked }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  const debounceRef = useRef(null);

  // BM-H396 (Vanessa, 8 Sep 2026): "I don't think we should leave anything up
  // to the team. Everything needs to be pre-populated as far as we can."
  //
  // Until now this modal opened on an empty search box, so linking required
  // the fee earner to know that the client had another file AND to know its
  // reference. The estate planning route produces exactly that case every
  // time: the review is one matter and the wills that follow it are another.
  // The backend now finds the client's other matters and says what each
  // would confer. The search box stays for the exception, not the rule.
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);

  // Trust-context guard: the path-param matter_reference shown in the
  // header is a snapshot we never mutate. If a future change ever
  // accidentally rewrites this prop, the UI will visibly disagree with
  // the API response and tests will catch it.
  const currentRefAtMount = useRef(matterRef);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/matters/${encodeURIComponent(matterRef)}/link-candidates`,
          { headers: getHeaders() },
        );
        if (cancelled) return;
        setSuggestions(res.data || null);
        // Pre-select the recommendation so the fee earner confirms rather
        // than searches. Never pre-select when the backend reports two
        // equally good files: that is a question for a human, and a silent
        // pick would be worse than the typing this replaces.
        const rec = res.data?.recommended_matter_reference;
        if (rec && !res.data?.ambiguous) {
          const row = (res.data.candidates || []).find(
            (c) => c.matter_reference === rec,
          );
          setSelected(row || { matter_reference: rec });
        }
      } catch (e) {
        // A failed lookup must never block the manual path that has always
        // worked. Say so quietly and leave the search box.
        if (!cancelled) setSuggestions({ lookup_failed: true });
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matterRef]);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // BM-H573 (21 Sep 2026): one in-flight search at a time. Each of these
    // fans out across three Mongo collections plus a SharePoint Graph call of
    // unbounded latency, so without this an earlier, slower query could land
    // its results under the reference the operator has since typed - which is
    // what the 21 September screenshot showed, unrelated matters listed under
    // a typed reference. The backend also echoes the query it answered, so
    // that is checked as well as the local flag.
    const controller = new AbortController();
    let stale = false;
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      setErr(null);
      try {
        // 18 Jun 2026 — Vanessa hotfix: pass active_only=false so the
        // smart-search resolver surfaces matters at conflict-passed /
        // OB-pending stages, not only those already past OB. Without
        // this flag the new matter the operator wants to link FROM
        // is hidden by the backend's default "active only" filter
        // (introduced in PR #607) — producing the misleading
        // "no matters found" Liezel/Himanie reported.
        //
        // 17 Jul 2026 — BM-884/885: ALSO pass matters_only=true. Without
        // it the resolver runs unified (contacts + matters) and the
        // limit=10 window gets consumed by contact rows, which we then
        // discard below (they have no matter_reference) — so real matters
        // like BL.VEC.002.1023 (Aida Gremi) / BL.VEC.002.1025 (Jaswinder
        // Sohi) never made it into the window and the modal showed
        // "No matters found" despite the matters existing at confidence 1.
        // matters_only=true makes the backend skip the contacts query
        // entirely (search_resolver.py: `if matters_only: contacts_raw=[]`),
        // so all limit slots are matters. Verified live: the standalone
        // SmartClientMatterSearch + useSmartSearchResolver already do this.
        const res = await axios.get(
          `${API}/smart-search`,
          {
            params: { q: query, limit: 10, active_only: false, matters_only: true },
            headers: getHeaders(),
            signal: controller.signal,
          },
        );
        if (stale) return;
        if (res.data?.query !== undefined && res.data.query !== query) return;
        const items = res.data?.results || res.data?.matters || [];
        // Only show rows that have a matter_reference (the resolver
        // also returns bare contact rows where matter_reference is
        // null — they can't be linked). Also exclude the current
        // matter so it can't link to itself.
        setResults(items.filter((m) => {
          const ref = m.matter_reference || m.matter_ref;
          return ref && ref !== matterRef;
        }));
      } catch (e) {
        if (stale || e?.code === "ERR_CANCELED" || e?.name === "CanceledError"
            || e?.name === "AbortError") return;
        setErr(errorMessage(e, "Search failed — try a more specific query"));
      } finally {
        if (!stale) setSearching(false);
      }
    }, 250);
    return () => {
      stale = true;
      try { controller.abort(); } catch (e) {}
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, matterRef]);

  const handleLink = async () => {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    setSuccess(null);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/link-ob`,
        { linked_matter_reference: selected.matter_reference || selected.matter_ref },
        { headers: getHeaders() },
      );
      // The API guarantees matter_reference is unchanged. Re-assert
      // here so a regression in the backend would surface as a UI
      // mismatch rather than a silent overwrite.
      if (res.data?.matter_reference !== currentRefAtMount.current) {
        setErr(
          `Refusing to apply — server returned matter_reference=${res.data?.matter_reference} ` +
          `but we asked to link ${currentRefAtMount.current}. Aborting and not refreshing.`,
        );
        return;
      }
      // Audit 05 P0-4: the backend already returns per-step outcomes for the
      // three follow-on operations (write OB satisfaction stub, copy OB PDFs,
      // advance the pipeline). Previous versions of this modal ignored those
      // fields, so the operator saw green "Linked X → Y" while the tracker
      // stayed at OB Sent — the origin of the "Link OB fixed many times,
      // still broken" pattern. Surface every failure explicitly.
      const warnings = linkOutcomeWarnings(res.data);
      setSuccess({
        linked: res.data.linked_ob_matter_reference,
        copied: res.data.fields_copied || [],
        skipped: res.data.fields_skipped_populated || [],
        pdfs_copied: res.data.pdf_files_copied || [],
        pdfs_skipped: res.data.pdf_files_skipped || [],
        thirdfort_inherited: res.data.thirdfort_fields_inherited || [],
        warnings,
      });
      if (onLinked) onLinked(res.data);
    } catch (e) {
      setErr(errorMessage(e, "Link failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="link-ob-modal-overlay" style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, color: "var(--green)" }}>Link OB</h3>
          <button onClick={onClose} aria-label="Close" style={iconBtnStyle}>×</button>
        </div>

        <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
          Link <strong data-testid="link-ob-current-ref">{matterRef}</strong> to an existing matter so the OB
          renderer pulls identity / contact / matter fields through. The
          current matter's reference is <strong>never</strong> overwritten.
        </div>

        {!success && (
          <>
            {/* BM-H396: what the system already knows, before anyone types. */}
            {loadingSuggestions && (
              <div style={hintStyle} data-testid="link-ob-candidates-loading">
                Looking for this client's other matters…
              </div>
            )}

            {!loadingSuggestions && suggestions?.candidates?.length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="link-ob-candidates">
                <label style={labelStyle}>
                  This client's other matters
                  {suggestions.person ? ` — ${suggestions.person}` : ""}
                </label>
                {suggestions.ambiguous && (
                  <div
                    style={{ ...hintStyle, color: "#fbbf24" }}
                    data-testid="link-ob-candidates-ambiguous"
                  >
                    More than one file would do the same job here, so none has
                    been pre-selected. Choose the right one.
                  </div>
                )}
                {suggestions.candidates.map((c) => {
                  const isSel =
                    selected &&
                    (selected.matter_reference || selected.matter_ref) ===
                      c.matter_reference;
                  return (
                    <button
                      key={c.matter_reference}
                      data-testid={`link-ob-candidate-${c.matter_reference}`}
                      onClick={() => setSelected(c)}
                      style={{
                        ...resultRowStyle,
                        background: isSel
                          ? "rgba(26, 171, 110, 0.18)"
                          : "transparent",
                        borderColor: isSel
                          ? "var(--green)"
                          : "var(--navy-border)",
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "var(--text)" }}>
                        {c.matter_reference}
                        {c.matter_reference ===
                          suggestions.recommended_matter_reference &&
                          !suggestions.ambiguous && (
                            <span
                              data-testid="link-ob-candidate-recommended"
                              style={{
                                marginLeft: 8,
                                fontSize: 10,
                                fontWeight: 700,
                                color: "var(--green)",
                              }}
                            >
                              SELECTED
                            </span>
                          )}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        {c.client_name || c.person || "(no client name)"}
                        {c.matter_type ? ` · ${c.matter_type}` : ""}
                      </div>
                      {/* What agreeing to this would actually do, in the
                          words the AML module already uses. */}
                      <div
                        style={{ fontSize: 11, color: "var(--text)", marginTop: 3 }}
                        data-testid={`link-ob-candidate-summary-${c.matter_reference}`}
                      >
                        {c.summary}
                      </div>
                      {c.aml_reason && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--text-muted)",
                            marginTop: 2,
                          }}
                        >
                          {c.aml_reason}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* BM-H573: matters found for this client that cannot be offered,
                shown with the reason instead of counted where nobody sees it.
                Not clickable. */}
            {!loadingSuggestions && suggestions?.not_offered?.length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="link-ob-not-offered">
                <label style={labelStyle}>
                  Found, but cannot be linked as it stands
                </label>
                {suggestions.not_offered.map((n) => (
                  <div
                    key={n.matter_reference}
                    data-testid={`link-ob-not-offered-${n.matter_reference}`}
                    style={{
                      ...resultRowStyle,
                      cursor: "default",
                      background: "transparent",
                      borderColor: "var(--navy-border)",
                      opacity: 0.85,
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "var(--text-muted)" }}>
                      {n.matter_reference}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {n.client_name || "(no client name)"}
                      {n.matter_type ? ` · ${n.matter_type}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 3 }}>
                      {n.reason}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loadingSuggestions &&
              suggestions &&
              !suggestions.lookup_failed &&
              !(suggestions.candidates?.length > 0) &&
              !(suggestions.not_offered?.length > 0) && (
                <div style={hintStyle} data-testid="link-ob-candidates-none">
                  {suggestions.reason ||
                    "No other matters were found for this client."}
                </div>
              )}

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle} htmlFor="link-ob-search">
                Search by matter reference or client name
              </label>
              <input
                id="link-ob-search"
                data-testid="link-ob-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. SLADL.006.0034 or Smith"
                style={inputStyle}
                autoFocus
              />
            </div>

            <div style={resultListStyle}>
              {searching && <div style={hintStyle}>Searching…</div>}
              {!searching && query.length >= 2 && results.length === 0 && (
                <div style={hintStyle}>No matters found for “{query}”.</div>
              )}
              {results.map((m) => {
                const ref = m.matter_reference || m.matter_ref;
                const isSel = selected && (selected.matter_reference || selected.matter_ref) === ref;
                return (
                  <button
                    key={ref}
                    data-testid={`link-ob-result-${ref}`}
                    onClick={() => setSelected(m)}
                    style={{
                      ...resultRowStyle,
                      background: isSel ? "rgba(26, 171, 110, 0.18)" : "transparent",
                      borderColor: isSel ? "var(--green)" : "var(--navy-border)",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "var(--text)" }}>{ref}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {m.client_name || m.matter_title || "(no client name)"}
                    </div>
                  </button>
                );
              })}
            </div>

            {err && <div data-testid="link-ob-error" style={errStyle}>{err}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
              <button
                data-testid="link-ob-confirm"
                onClick={handleLink}
                disabled={!selected || busy}
                style={{
                  ...confirmBtnStyle,
                  background: selected ? "var(--green)" : "#374151",
                  cursor: selected && !busy ? "pointer" : "not-allowed",
                }}
              >
                {busy ? "Linking…" : "Link OB"}
              </button>
            </div>
          </>
        )}

        {success && (
          <div data-testid="link-ob-success" style={{ marginTop: 12 }}>
            {/* Audit 05 P0-4: banner colour reflects whether every follow-on
                step succeeded, not just whether the link write returned 200. */}
            <div
              style={{
                color: success.warnings.length === 0 ? "var(--green)" : "#f59e0b",
                fontWeight: 700,
              }}
              data-testid={
                success.warnings.length === 0
                  ? "link-ob-success-clean"
                  : "link-ob-success-partial"
              }
            >
              {success.warnings.length === 0 ? "Linked" : "Linked with warnings:"}{" "}
              {currentRefAtMount.current} → {success.linked}
            </div>

            {success.warnings.length > 0 && (
              <div style={warningBoxStyle} data-testid="link-ob-warnings">
                {success.warnings.map((w) => (
                  <div key={w.key} style={warningRowStyle} data-testid={`link-ob-warning-${w.key}`}>
                    <div style={{ fontWeight: 700, color: "#fbbf24" }}>{w.label}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                      {w.detail}
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                  The link itself persisted. Check the pipeline tracker before treating this matter as OB-complete.
                </div>
              </div>
            )}

            {success.copied.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-muted)" }}>
                Copied: {success.copied.join(", ")}
              </div>
            )}
            {success.skipped.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
                Kept (already populated): {success.skipped.join(", ")}
              </div>
            )}
            {success.pdfs_copied.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
                OB files copied: {success.pdfs_copied.length}
              </div>
            )}
            {success.thirdfort_inherited.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-muted)" }}>
                Thirdfort fields inherited: {success.thirdfort_inherited.join(", ")}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button onClick={onClose} style={confirmBtnStyle}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Button (action-row entry point) ──────────────────────────────────────────
export function LinkOBButton({ matterRef, linkedRef, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleUnlink = async () => {
    // Issue #2957: unlink now also takes back the OB completion the link
    // recorded, so the confirm says so.
    if (!window.confirm(
      `Unlink ${matterRef} from ${linkedRef}? Onboarding on ${matterRef} will `
      + `no longer be marked complete, so it can be sent to this matter's own `
      + `clients. Already-copied fields stay in place.`,
    )) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/unlink-ob`,
        {},
        { headers: getHeaders() },
      );
      // Trust-context: confirm matter_reference unchanged.
      if (res.data?.matter_reference !== matterRef) {
        setErr(
          `Refusing — server returned matter_reference=${res.data?.matter_reference} ` +
          `but we asked to unlink ${matterRef}.`,
        );
        return;
      }
      if (res.data?.stage_note) window.alert(res.data.stage_note);
      if (onSuccess) onSuccess(matterRef);
    } catch (e) {
      setErr(errorMessage(e, "Unlink failed"));
    } finally {
      setBusy(false);
    }
  };

  // BM-H282: re-run the link on a matter that is ALREADY linked.
  //
  // A link inherits whatever the source matter had at the moment it was
  // made. Link a matter to a sibling whose Thirdfort report has not been
  // approved yet and the AML half of the link correctly inherits nothing.
  // When the approval lands later, the link has to run again to carry it
  // across and copy the report. Before this existed the only route was
  // unlink then link, which threw away the original link audit row and
  // its timestamp. Re-running is idempotent: the backend refuses to
  // overwrite a genuine onboarding or AML record of the matter's own.
  const handleRelink = async () => {
    if (!window.confirm(
      `Re-run the link from ${matterRef} to ${linkedRef}? This pulls across `
      + `any onboarding and AML evidence that has been added to ${linkedRef} `
      + `since the link was made. Nothing already on ${matterRef} is `
      + `overwritten.`,
    )) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/link-ob`,
        { linked_matter_reference: linkedRef },
        { headers: getHeaders() },
      );
      if (res.data?.matter_reference && res.data.matter_reference !== matterRef) {
        setErr(
          `Refusing. Server returned matter_reference=${res.data?.matter_reference} `
          + `but we asked to re-link ${matterRef}.`,
        );
        return;
      }
      const warnings = linkOutcomeWarnings(res.data);
      if (warnings.length > 0) {
        window.alert(
          "Link re-run, with warnings:\n\n"
          + warnings.map((w) => `${w.label}\n${w.detail}`).join("\n\n"),
        );
      }
      if (onSuccess) onSuccess(matterRef);
    } catch (e) {
      setErr(errorMessage(e, "Re-run of the link failed"));
    } finally {
      setBusy(false);
    }
  };

  if (linkedRef) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="link-ob-linked-pill">
        <span style={pillStyle}>Linked OB → {linkedRef}</span>
        <button
          data-testid="relink-ob-button"
          onClick={(e) => { e.stopPropagation(); handleRelink(); }}
          disabled={busy}
          style={ghostBtnStyle}
          title="Re-run the link and pull across onboarding and AML evidence added to the linked matter since"
        >
          {busy ? "Working…" : "Refresh inheritance"}
        </button>
        <button
          data-testid="unlink-ob-button"
          onClick={(e) => { e.stopPropagation(); handleUnlink(); }}
          disabled={busy}
          style={ghostBtnStyle}
          title="Unlink — already-copied fields stay in place"
        >
          {busy ? "Unlinking…" : "Unlink"}
        </button>
        {err && <span style={{ color: "var(--red)", fontSize: 11 }}>{err}</span>}
      </span>
    );
  }

  return (
    <>
      <button
        data-testid="link-ob-button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={brandBtnStyle}
        title="Link OB — pull identity / contact / matter fields from an existing matter"
      >
        🔗 Link OB
      </button>
      {open && (
        <LinkOBModal
          matterRef={matterRef}
          onClose={() => setOpen(false)}
          onLinked={() => {
            setOpen(false);
            if (onSuccess) onSuccess(matterRef);
          }}
        />
      )}
    </>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000,
};
const modalStyle = {
  background: "var(--navy)", color: "var(--text)",
  border: "1px solid var(--navy-border)", borderRadius: 8,
  padding: 16, width: "min(540px, 92vw)", maxHeight: "85vh", overflow: "auto",
  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
};
const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: "var(--text-muted)", marginBottom: 4,
};
const inputStyle = {
  width: "100%", padding: "8px 10px", fontSize: 13,
  background: "var(--navy-deep)", color: "var(--text)",
  border: "1px solid var(--navy-border)", borderRadius: 4,
  outline: "none", boxSizing: "border-box",
};
const resultListStyle = {
  marginTop: 8, maxHeight: 240, overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 4,
};
const resultRowStyle = {
  textAlign: "left", padding: "8px 10px", borderRadius: 4,
  border: "1px solid var(--navy-border)", cursor: "pointer",
  color: "var(--text)",
};
const hintStyle = {
  fontSize: 11, color: "var(--text-muted)", padding: "8px 0",
};
const errStyle = {
  marginTop: 8, padding: "6px 10px", borderRadius: 4,
  background: "rgba(239, 68, 68, 0.12)", border: "1px solid #ef4444",
  color: "#fca5a5", fontSize: 12,
};
const warningBoxStyle = {
  marginTop: 10, padding: "8px 10px", borderRadius: 4,
  background: "rgba(245, 158, 11, 0.10)", border: "1px solid #f59e0b",
};
const warningRowStyle = {
  marginBottom: 6,
};
const pillStyle = {
  padding: "3px 8px", borderRadius: 999, fontSize: 11,
  background: "rgba(26, 171, 110, 0.14)", color: "var(--green)",
  border: "1px solid var(--green)", whiteSpace: "nowrap",
};
const brandBtnStyle = {
  background: "var(--brand-dim)", border: "1px solid var(--brand-line)",
  borderRadius: 4, color: "var(--brand-text)", fontSize: 11, fontWeight: 700,
  padding: "3px 8px", cursor: "pointer", whiteSpace: "nowrap",
};
const ghostBtnStyle = {
  background: "transparent", border: "1px solid #374151", borderRadius: 4,
  color: "var(--text-muted)", fontSize: 11, padding: "3px 8px",
  cursor: "pointer",
};
const cancelBtnStyle = {
  background: "transparent", border: "1px solid #374151", borderRadius: 4,
  color: "var(--text-muted)", fontSize: 12, padding: "6px 12px",
  cursor: "pointer",
};
const confirmBtnStyle = {
  border: "none", borderRadius: 4, color: "#fff",
  fontSize: 12, fontWeight: 700, padding: "6px 14px",
  cursor: "pointer", background: "var(--green)",
};
const iconBtnStyle = {
  background: "transparent", border: "none", color: "var(--text-muted)",
  fontSize: 22, lineHeight: 1, cursor: "pointer",
};
